import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePermission } from "../hooks/usePermission";
import { apiFetch } from "../api/apiFetch";
import ViewAsMenu from "./ViewAsMenu";

export default function Navbar({ user, onLogout }) {
  const { hasPermission, hasAnyPermission, hasInventoryAccess, hasSecurityAccess, hasResultsAccess, hasIncomeAccess } = usePermission();
  const [stockMenuOpen, setStockMenuOpen] = useState(false);
  const [securityMenuOpen, setSecurityMenuOpen] = useState(false);
  const [resultsMenuOpen, setResultsMenuOpen] = useState(false);
  const [timesheetsMenuOpen, setTimesheetsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const [showEnvTooltip, setShowEnvTooltip] = useState(false);
  const stockMenuRef = useRef(null);
  const securityMenuRef = useRef(null);
  const resultsMenuRef = useRef(null);
  const timesheetsMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  // Permission checks for inventory sub-items
  const canViewStockCounts = hasPermission("stock_counts.view");
  const canViewPurchases = hasPermission("purchases.view");
  const canViewSales = hasPermission("sales.view");
  const canViewProducts = hasPermission("products.view");
  const canViewBrands = hasPermission("brands.view");
  const canViewLocations = hasPermission("locations.view");
  const canViewSuppliers = hasPermission("suppliers.view");

  // Show "Beheer" section only if user can see at least one item
  const showBeheerSection = canViewProducts || canViewBrands || canViewLocations || canViewSuppliers;

  // Results module permissions
  const canViewResults = hasPermission("results.view");
  const canImportResults = hasPermission("results.import");
  const canManageResults = hasPermission("results.manage");

  // Income (Boekje Saskia) permission - link now lives under the Resultaten dropdown
  const canViewBoekjeSaskia = hasPermission("boekje_saskia.view");

  // Prestaties personeel (contractlijnen beheren)
  const canManageContracts = hasPermission("contracts.manage");
  const canApproveTimesheets = hasAnyPermission("timesheets.approve", "timesheets.release");
  const canSeeTimesheets = hasAnyPermission("timesheets.view_own", "timesheets.view_all");

  // Fetch environment info on mount
  useEffect(() => {
    apiFetch("/environment")
      .then(setEnvInfo)
      .catch((err) => console.error("Failed to fetch environment info:", err));
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (stockMenuRef.current && !stockMenuRef.current.contains(event.target)) {
        setStockMenuOpen(false);
      }
      if (securityMenuRef.current && !securityMenuRef.current.contains(event.target)) {
        setSecurityMenuOpen(false);
      }
      if (timesheetsMenuRef.current && !timesheetsMenuRef.current.contains(event.target)) {
        setTimesheetsMenuOpen(false);
      }
      if (resultsMenuRef.current && !resultsMenuRef.current.contains(event.target)) {
        setResultsMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    onLogout();
  };

  const getInitials = (user) => {
    if (!user) return "U";
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user.last_name) return user.last_name.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setStockMenuOpen(false);
    setSecurityMenuOpen(false);
    setResultsMenuOpen(false);
    setTimesheetsMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-md">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/bonamuze-logo.png"
                alt="Bonamuze"
                className="navbar-logo-icon"
              />
              <span className="text-xl text-bonamuze text-bonamuze-styled tracking-wide">
                Bonamuze
              </span>
            </Link>
            {/* Environment indicator - only shown when not in production */}
            {envInfo && !envInfo.is_production && (
              <div className="relative ml-3">
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 cursor-help border border-amber-300"
                  onMouseEnter={() => setShowEnvTooltip(true)}
                  onMouseLeave={() => setShowEnvTooltip(false)}
                >
                  {envInfo.environment === 'local' ? 'LOCAL' : 'TEST'}
                </span>
                {showEnvTooltip && (
                  <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50">
                    <div className="font-semibold mb-1">Testomgeving</div>
                    <div className="text-gray-300">
                      <div>Database: <span className="font-mono">{envInfo.database_name}</span></div>
                      <div>Host: <span className="font-mono">{envInfo.database_host}</span></div>
                    </div>
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop Menu Items - Hidden on mobile */}
          <div className="hidden sm:flex items-center space-x-8">
            {/* Voorraden Dropdown - Only show if user has any inventory permission */}
            {hasInventoryAccess && (
              <div className="relative" ref={stockMenuRef}>
                <button
                  onClick={() => setStockMenuOpen(!stockMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Voorraden
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform ${stockMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {stockMenuOpen && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {/* Overzicht - always visible when menu is visible */}
                      <Link
                        to="/inventory-overview"
                        onClick={() => setStockMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Overzicht
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>

                      {/* Conditional sub-items */}
                      {canViewStockCounts && (
                        <Link
                          to="/stock-counts"
                          onClick={() => setStockMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Tellingen
                        </Link>
                      )}
                      {canViewPurchases && (
                        <Link
                          to="/purchases"
                          onClick={() => setStockMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Aankopen
                        </Link>
                      )}
                      {canViewSales && (
                        <Link
                          to="/sales"
                          onClick={() => setStockMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Verkopen
                        </Link>
                      )}

                      {/* Beheer section - only show header if any items visible */}
                      {showBeheerSection && (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                            Beheer
                          </div>
                          {canViewProducts && (
                            <Link
                              to="/products"
                              onClick={() => setStockMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 pl-8"
                            >
                              Producten
                            </Link>
                          )}
                          {canViewBrands && (
                            <Link
                              to="/brands"
                              onClick={() => setStockMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 pl-8"
                            >
                              Merken
                            </Link>
                          )}
                          {canViewLocations && (
                            <Link
                              to="/locations"
                              onClick={() => setStockMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 pl-8"
                            >
                              Locaties
                            </Link>
                          )}
                          {canViewSuppliers && (
                            <Link
                              to="/suppliers"
                              onClick={() => setStockMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 pl-8"
                            >
                              Leveranciers
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Resultaten Dropdown - Only show if user has results or income (Boekje Saskia) access */}
            {(hasResultsAccess || hasIncomeAccess) && (
              <div className="relative" ref={resultsMenuRef}>
                <button
                  onClick={() => setResultsMenuOpen(!resultsMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Resultaten
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform ${resultsMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {resultsMenuOpen && (
                  <div className="absolute left-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {canViewResults && (
                        <Link
                          to="/results/inkomstenfile"
                          onClick={() => setResultsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Inkomstenfile
                        </Link>
                      )}
                      {canViewResults && (
                        <Link
                          to="/results/consultations"
                          onClick={() => setResultsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Consultaties
                        </Link>
                      )}
                      {(canViewBoekjeSaskia || canImportResults) && (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          {canViewBoekjeSaskia && (
                            <Link
                              to="/inkomsten/boekje-saskia"
                              onClick={() => setResultsMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Boekje Saskia
                            </Link>
                          )}
                          {canImportResults && (
                            <Link
                              to="/results/manual"
                              onClick={() => setResultsMenuOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Weekingave
                            </Link>
                          )}
                        </>
                      )}
                      {canManageResults && (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          <Link
                            to="/results/config"
                            onClick={() => setResultsMenuOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Configuratie
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Timesheets Dropdown - items enkel zichtbaar met het juiste recht */}
            {(canSeeTimesheets || canApproveTimesheets || canManageContracts) && (
              <div className="relative" ref={timesheetsMenuRef}>
                <button
                  onClick={() => setTimesheetsMenuOpen(!timesheetsMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Timesheets
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform ${timesheetsMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {timesheetsMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {canSeeTimesheets && (
                        <Link
                          to="/timesheets"
                          onClick={() => setTimesheetsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Prestaties ingeven
                        </Link>
                      )}
                      {canApproveTimesheets && (
                        <Link
                          to="/timesheets/approvals"
                          onClick={() => setTimesheetsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Prestaties goedkeuren
                        </Link>
                      )}
                      {canManageContracts && (
                        <Link
                          to="/timesheets/admin"
                          onClick={() => setTimesheetsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Personeel beheren
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Veiligheidsbeheer Dropdown - Only show for users with security permissions */}
            {hasSecurityAccess && (
              <div className="relative" ref={securityMenuRef}>
                <button
                  onClick={() => setSecurityMenuOpen(!securityMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Veiligheidsbeheer
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform ${securityMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {securityMenuOpen && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {hasAnyPermission("users.view", "roles.view") && (
                        <Link
                          to="/security-overview"
                          onClick={() => setSecurityMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Overzicht
                        </Link>
                      )}
                      {hasPermission("users.view") && (
                        <Link
                          to="/users"
                          onClick={() => setSecurityMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Gebruikers
                        </Link>
                      )}
                      {hasPermission("roles.view") && (
                        <Link
                          to="/roles"
                          onClick={() => setSecurityMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Rollen
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right side: User menu (desktop) and hamburger (mobile) */}
          <div className="flex items-center gap-2">
            {/* Mobile hamburger button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            <ViewAsMenu />

            {/* User Avatar & Dropdown - Always visible */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center focus:outline-none"
              >
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                  {getInitials(user)}
                </div>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs text-gray-500 border-b">
                      {user?.displayName || user?.email}
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Mijn profiel
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Uitloggen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Slide down panel */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {/* Voorraden Section */}
            {hasInventoryAccess && (
              <div className="border-b border-gray-100 pb-2 mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Voorraden</div>
                <Link
                  to="/inventory-overview"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Overzicht
                </Link>
                {canViewStockCounts && (
                  <Link
                    to="/stock-counts"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Tellingen
                  </Link>
                )}
                {canViewPurchases && (
                  <Link
                    to="/purchases"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Aankopen
                  </Link>
                )}
                {canViewSales && (
                  <Link
                    to="/sales"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Verkopen
                  </Link>
                )}
                {showBeheerSection && (
                  <>
                    <div className="text-xs font-semibold text-gray-400 uppercase mt-3 mb-1 pl-3">Beheer</div>
                    {canViewProducts && (
                      <Link
                        to="/products"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded pl-6"
                      >
                        Producten
                      </Link>
                    )}
                    {canViewBrands && (
                      <Link
                        to="/brands"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded pl-6"
                      >
                        Merken
                      </Link>
                    )}
                    {canViewLocations && (
                      <Link
                        to="/locations"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded pl-6"
                      >
                        Locaties
                      </Link>
                    )}
                    {canViewSuppliers && (
                      <Link
                        to="/suppliers"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded pl-6"
                      >
                        Leveranciers
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Resultaten Section */}
            {(hasResultsAccess || hasIncomeAccess) && (
              <div className="border-b border-gray-100 pb-2 mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Resultaten</div>
                {canViewResults && (
                  <Link
                    to="/results/inkomstenfile"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Inkomstenfile
                  </Link>
                )}
                {canViewResults && (
                  <Link
                    to="/results/consultations"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Consultaties
                  </Link>
                )}
                {canViewBoekjeSaskia && (
                  <Link
                    to="/inkomsten/boekje-saskia"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Boekje Saskia
                  </Link>
                )}
                {canImportResults && (
                  <Link
                    to="/results/manual"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Weekingave
                  </Link>
                )}
                {canManageResults && (
                  <Link
                    to="/results/config"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Configuratie
                  </Link>
                )}
              </div>
            )}

            {/* Prestaties personeel Section */}
            {(canSeeTimesheets || canApproveTimesheets || canManageContracts) && (
              <div className="border-b border-gray-100 pb-2 mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Timesheets</div>
                {canSeeTimesheets && (
                  <Link
                    to="/timesheets"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Prestaties ingeven
                  </Link>
                )}
                {canApproveTimesheets && (
                  <Link
                    to="/timesheets/approvals"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Prestaties goedkeuren
                  </Link>
                )}
                {canManageContracts && (
                  <Link
                    to="/timesheets/admin"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Personeel beheren
                  </Link>
                )}
              </div>
            )}

            {/* Veiligheidsbeheer Section */}
            {hasSecurityAccess && (
              <div className="border-b border-gray-100 pb-2 mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Veiligheidsbeheer</div>
                {hasAnyPermission("users.view", "roles.view") && (
                  <Link
                    to="/security-overview"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Overzicht
                  </Link>
                )}
                {hasPermission("users.view") && (
                  <Link
                    to="/users"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Gebruikers
                  </Link>
                )}
                {hasPermission("roles.view") && (
                  <Link
                    to="/roles"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Rollen
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
